<template>
  <div class="opportunities-phone-list">
    <BasePhoneList
      :items="opportunities"
      :selecting="selecting"
      :sort-open="sortOpen"
      :sort-options="sortOptions"
      :sort-key="sortKey"
      :has-more="hasMore"
      :loading-more="loadingMore"
      :load-more-error="loadMoreError"
      :pending="pending"
      @update:sortOpen="$emit('update:sortOpen', $event)"
      @sort="$emit('sort', $event)"
      @load-more="$emit('load-more')"
    >
      <template #item="{ item: opportunity, selected, toggle }">
        <OpportunityListItem
          :key="opportunity.id"
          :opportunity="opportunity"
          :selectable="selecting"
          :selected="selected"
          :status-updating="updatingStatusIds.includes(opportunity.id)"
          :show-votes="showVotes"
          :show-documents="showDocuments"
          :syncs="opportunity.syncs"
          @open="$emit('open', opportunity)"
          @toggle-select="toggle(opportunity.id)"
          @more="openSheet(opportunity)"
        />
      </template>
      <template #selection-actions="{ items, clear }">
        <OpportunitiesActionManager
          :opportunities="items"
          @refresh-opportunities="afterBulkAction(clear)"
          @download-documents-started="clear"
        />
      </template>
    </BasePhoneList>

    <BaseBottomSheet
      v-model="actionsSheetOpen"
      :title="sheetTitle"
      :can-go-back="view !== 'actions'"
      @back="view = 'actions'"
    >
      <template v-if="actionsOpportunity">
        <ul v-if="view === 'status'" class="base-bottom-sheet__menu" role="radiogroup">
          <li v-for="status in statuses" :key="status.id">
            <button
              type="button"
              role="radio"
              :aria-checked="String(isCurrentStatus(status))"
              :class="{ 'is-active': isCurrentStatus(status) }"
              @click="changeStatus(status)"
            >
              <span class="base-bottom-sheet__swatch" :style="{ background: status.color }"></span>
              {{ status.name }}
              <BaseIcon v-if="isCurrentStatus(status)" name="check" class="base-bottom-sheet__menu-check" />
            </button>
          </li>
        </ul>

        <!-- Labels and assignees: picked here, saved when the sheet closes, as the desktop menus do. -->
        <BaseSheetChecklist
          v-else-if="view === 'labels' || view === 'assignees'"
          v-model="draft"
          :choices="choices"
          :label="sheetTitle"
          @done="actionsSheetOpen = false"
        >
          <template v-if="view === 'labels'" #default="{ choice }">
            <BaseTag :background="choice.color">{{ choice.name }}</BaseTag>
          </template>
        </BaseSheetChecklist>

        <template v-else>
          <dl class="base-bottom-sheet__facts">
            <div>
              <dt>{{ $t('page.opportunities.data-table-titles.author') }}</dt>
              <dd>{{ createdLine }}</dd>
            </div>
            <div v-if="closingDate">
              <dt>{{ $t('page.tenders.data-table-titles.closing') }}</dt>
              <dd>{{ formatUserShortDate(closingDate) }}</dd>
            </div>
          </dl>
          <ul class="base-bottom-sheet__menu">
            <!-- These three show their choices in the sheet; the chevron says so. -->
            <li>
              <button type="button" @click="view = 'status'">
                <BaseIcon name="label-variant-outline" />
                {{ $t('actions.change-status') }}
                <BaseIcon name="chevron-right" class="base-bottom-sheet__menu-end" />
              </button>
            </li>
            <li>
              <button type="button" @click="openPicker('labels')">
                <BaseIcon name="tag" />
                {{ $t('page.opportunities.data-table-titles.labels') }}
                <BaseIcon name="chevron-right" class="base-bottom-sheet__menu-end" />
              </button>
            </li>
            <li>
              <button type="button" @click="openPicker('assignees')">
                <BaseIcon name="users" />
                {{ $t('page.tenders.assignation.assign-to') }}
                <BaseIcon name="chevron-right" class="base-bottom-sheet__menu-end" />
              </button>
            </li>
            <li v-if="showVotes">
              <!-- As in the desktop table: a vote to cast, or the votes so far. -->
              <button v-if="hasVotes" type="button" @click="act('go-nogo')">
                <BaseIcon name="vote" />
                {{ $t('page.opportunity.mode.go-nogo') }}
              </button>
              <button v-else type="button" @click="act('vote')">
                <BaseIcon name="vote" />
                {{ $t('page.opportunities.vote') }}
              </button>
            </li>
            <li v-for="oppSync in actionsOpportunity.syncs || []" :key="oppSync.profile.id">
              <button type="button" class="opportunities-phone-list__sync" @click="synchronize(oppSync)">
                <BaseIcon :name="syncIcon(oppSync)" />
                <span class="opportunities-phone-list__sync-text">{{ syncLabel(oppSync) }}</span>
              </button>
            </li>
            <li v-if="actionsOpportunity.tender">
              <button type="button" @click="act('view-tender')">
                <BaseIcon name="file-text" />
                {{ $t('page.opportunities.view-tender') }}
              </button>
            </li>
          </ul>
        </template>
      </template>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneList from '@/components/base/BasePhoneList.vue';
import BaseSheetChecklist from '@/components/base/BaseSheetChecklist.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import OpportunitiesActionManager from '@/components/page-opportunities/OpportunitiesActionManager.vue';
import OpportunityListItem from '@/components/page-opportunities/OpportunityListItem.vue';
import { areSameFlatArray } from '@/util/array-util';
import { formatUserDate, formatUserShortDate } from '@/util/date-util';
import { getEntitySyncIcon, getEntitySyncLabel } from '@/util/entity-sync-util';
import { getOppSortingField, getSortingOrder } from '@/util/sorting-util';

// The sortable columns of the desktop table, as one-tap choices.
const SORT_OPTIONS = [
  { key: 'created-desc', field: 'createdAt', desc: true },
  { key: 'created-asc', field: 'createdAt', desc: false },
  { key: 'closing-asc', field: 'oppClosingDate', desc: false },
  { key: 'closing-desc', field: 'oppClosingDate', desc: true },
];

// The phone version of the opportunities table: compact rows, and in each row's sheet what the
// desktop table's columns show and change.
export default {
  name: 'OpportunitiesPhoneList',
  components: {
    BaseBottomSheet,
    BaseIcon,
    BasePhoneList,
    BaseSheetChecklist,
    BaseTag,
    OpportunitiesActionManager,
    OpportunityListItem,
  },
  props: {
    // With their `syncs`, as the desktop table has them.
    opportunities: {
      type: Array,
      required: true,
    },
    statuses: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    // Who the author ids name.
    users: {
      type: Array,
      default: () => [],
    },
    // Who the reader may assign.
    assignableUsers: {
      type: Array,
      default: () => [],
    },
    // Rows whose status is being saved.
    updatingStatusIds: {
      type: Array,
      default: () => [],
    },
    showVotes: {
      type: Boolean,
      default: false,
    },
    showDocuments: {
      type: Boolean,
      default: false,
    },
    selecting: {
      type: Boolean,
      default: false,
    },
    sortOpen: {
      type: Boolean,
      default: false,
    },
    sortingField: {
      type: String,
      default: 'created_at',
    },
    sortingOrder: {
      type: String,
      default: 'desc',
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      actionsOpportunity: null,
      // What the sheet shows: the actions, or one of the pickers.
      view: 'actions',
      // The labels or assignees picked so far, by id.
      // Each picker's ticks since the sheet opened, kept while going back to its actions.
      drafts: {},
    };
  },
  computed: {
    sortOptions() {
      return SORT_OPTIONS.map((option) => ({
        ...option,
        label: this.$t(`page.opportunities.phone-list.sort-options.${option.key}`),
      }));
    },
    sortKey() {
      const current = SORT_OPTIONS.find(
        (option) =>
          getOppSortingField(option.field) === this.sortingField && getSortingOrder(option.desc) === this.sortingOrder,
      );
      return current ? current.key : '';
    },
    actionsSheetOpen: {
      get() {
        return !!this.actionsOpportunity;
      },
      set(open) {
        if (open) return;
        this.savePicked();
        this.actionsOpportunity = null;
        this.view = 'actions';
        this.drafts = {};
      },
    },
    // What's ticked on the level shown, labels or assignees.
    draft: {
      get() {
        return this.drafts[this.view] || [];
      },
      set(ids) {
        this.$set(this.drafts, this.view, ids);
      },
    },
    sheetTitle() {
      if (!this.actionsOpportunity) return '';
      if (this.view === 'status') return this.$t('search-filters.status.name');
      if (this.view === 'labels') return this.$t('page.opportunities.data-table-titles.labels');
      if (this.view === 'assignees') return this.$t('page.opportunities.data-table-titles.assignees');
      return this.actionsOpportunity.name;
    },
    hasVotes() {
      return !!this.actionsOpportunity.finalDecision || this.actionsOpportunity.votes.length > 0;
    },
    createdLine() {
      const { createdAt, authorId } = this.actionsOpportunity;
      const author = this.users.find((user) => user.id === authorId);
      return `${formatUserDate(createdAt)} · ${author ? author.fullName : this.$t('unknown.user')}`;
    },
    // The tender's closing when it has one, as in the desktop table.
    closingDate() {
      const { tender, endDate } = this.actionsOpportunity;
      if (tender && tender.tenderPeriod && tender.tenderPeriod.endDate) return tender.tenderPeriod.endDate;
      return endDate;
    },
    // The picker's choices, by name.
    choices() {
      const choices =
        this.view === 'labels'
          ? this.labels
          : this.assignableUsers.map((user) => ({ id: user.id, name: user.fullName, detail: user.email }));
      return [...choices].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
  },
  methods: {
    formatUserShortDate,
    syncIcon(oppSync) {
      return getEntitySyncIcon(oppSync.profile, oppSync.sync);
    },
    syncLabel(oppSync) {
      return getEntitySyncLabel(oppSync.profile, oppSync.sync);
    },
    openSheet(opportunity) {
      this.view = 'actions';
      this.actionsOpportunity = opportunity;
    },
    isCurrentStatus(status) {
      return !!this.actionsOpportunity.status && this.actionsOpportunity.status.id === status.id;
    },
    changeStatus(status) {
      const opportunity = this.actionsOpportunity;
      this.actionsSheetOpen = false;
      if (!opportunity.status || opportunity.status.id !== status.id) this.$emit('status-change', opportunity, status);
    },
    pickedIds(view) {
      const opportunity = this.actionsOpportunity;
      return view === 'labels'
        ? (opportunity.labels || []).map((label) => label.labelId)
        : (opportunity.assignees || []).map((assignee) => assignee.userId);
    },
    openPicker(view) {
      if (!this.drafts[view]) this.$set(this.drafts, view, this.pickedIds(view));
      this.view = view;
    },
    savePicked() {
      Object.entries(this.drafts).forEach(([view, ids]) => {
        if (areSameFlatArray(this.pickedIds(view), [...ids])) return;
        this.$emit(view === 'labels' ? 'labels-change' : 'assign', this.actionsOpportunity, [...ids]);
      });
    },
    synchronize(oppSync) {
      const opportunity = this.actionsOpportunity;
      this.actionsSheetOpen = false;
      this.$emit('synchronize', oppSync, opportunity);
    },
    act(action) {
      const opportunity = this.actionsOpportunity;
      this.actionsSheetOpen = false;
      this.$emit(action, opportunity);
    },
    afterBulkAction(clearSelection) {
      clearSelection();
      this.$emit('refresh');
    },
  },
};
</script>

<style lang="scss">
.opportunities-phone-list {
  // The desktop toolbar floats over the table header; here it sits in the selection bar, on
  // as many lines as the screen needs.
  .action-manager {
    position: static;
    z-index: auto;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 4px;
    width: 100%;
    padding: 0;
    box-shadow: none;
  }

  // The sync's state can take a few lines: a failure says why.
  .base-bottom-sheet__menu &__sync {
    padding-top: 8px;
    padding-bottom: 8px;
    line-height: 1.3;
  }

  &__sync-text {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow-wrap: anywhere;
  }
}
</style>
